/**
 * The icon set, inline.
 *
 * A dashboard this size uses about sixty glyphs; the smallest icon package that
 * covers them is ~40KB over the wire before tree-shaking gets confused by the
 * barrel export. These are stroke paths on a 24-grid, they inherit `color`, and
 * they cost nothing to load — which matters, because a lot of these merchants
 * open this screen on mobile data.
 */

import type { SVGProps } from 'react'

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  size?: number
}

function icon(path: React.ReactNode, filled = false) {
  return function Icon({ size = 18, ...rest }: IconProps): React.ReactNode {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={filled ? 'currentColor' : 'none'}
        stroke={filled ? 'none' : 'currentColor'}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        {...rest}
      >
        {path}
      </svg>
    )
  }
}

// ── Brand ───────────────────────────────────────────────────────────────────

export const BoltIcon = icon(<path d="M13 2 4.1 12.7a1 1 0 0 0 .8 1.6H11l-1 7.7 8.9-10.7a1 1 0 0 0-.8-1.6H12z" />, true)

// ── Navigation ──────────────────────────────────────────────────────────────

export const HomeIcon = icon(
  <>
    <path d="m3 10 9-7 9 7v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M9 21v-8h6v8" />
  </>,
)

export const StoreIcon = icon(
  <>
    <path d="M4 9h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
    <path d="M3 9 5 4h14l2 5" />
    <path d="M9 9a3 3 0 0 0 6 0" />
  </>,
)

export const BagIcon = icon(
  <>
    <path d="M6 7h12l1 13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z" />
    <path d="M9 10V6a3 3 0 0 1 6 0v4" />
  </>,
)

export const UsersIcon = icon(
  <>
    <path d="M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="3.2" />
    <path d="M22 20v-2a4 4 0 0 0-3-3.8" />
    <path d="M16 3.2A4 4 0 0 1 16 11" />
  </>,
)

export const UserIcon = icon(
  <>
    <path d="M19 21v-2a5 5 0 0 0-5-5h-4a5 5 0 0 0-5 5v2" />
    <circle cx="12" cy="7" r="4" />
  </>,
)

export const CartIcon = icon(
  <>
    <circle cx="9" cy="20" r="1.4" />
    <circle cx="18" cy="20" r="1.4" />
    <path d="M2 3h2.5l2.3 12.3a1.5 1.5 0 0 0 1.5 1.2h9.1a1.5 1.5 0 0 0 1.5-1.2L21 7H5.4" />
  </>,
)

export const CardIcon = icon(
  <>
    <rect x="2" y="5" width="20" height="14" rx="2.5" />
    <path d="M2 10h20" />
  </>,
)

export const WalletIcon = icon(
  <>
    <path d="M20 8V6.5A1.5 1.5 0 0 0 18.5 5H5a2 2 0 0 0 0 4h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6.5" />
    <circle cx="17" cy="14" r="1.2" fill="currentColor" stroke="none" />
  </>,
)

export const BellIcon = icon(
  <>
    <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7" />
    <path d="M13.7 20a2 2 0 0 1-3.4 0" />
  </>,
)

export const ChartIcon = icon(
  <>
    <path d="M3 3v16a2 2 0 0 0 2 2h16" />
    <path d="M7 15v-3" />
    <path d="M12 15V8" />
    <path d="M17 15v-6" />
  </>,
)

export const PlugIcon = icon(
  <>
    <path d="M3 8V5a2 2 0 0 1 2-2h3" />
    <path d="M16 3h3a2 2 0 0 1 2 2v3" />
    <path d="M21 16v3a2 2 0 0 1-2 2h-3" />
    <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
    <rect x="8.5" y="8.5" width="7" height="7" rx="1.5" />
  </>,
)

export const GearIcon = icon(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7.5 19.4l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3.6 14H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 7.5l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.6 1.6 0 0 0 10 3.6V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.5 1.5l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />
  </>,
)

export const CheckoutIcon = icon(
  <>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M3 9h18" />
    <path d="m8.5 14.5 2 2 4-4" />
  </>,
)

export const MegaphoneIcon = icon(
  <>
    <path d="M3 11v2a1 1 0 0 0 1 1h2l6 4V6L6 10H4a1 1 0 0 0-1 1" />
    <path d="M17 8.5a4.5 4.5 0 0 1 0 7" />
    <path d="M20 6a8 8 0 0 1 0 12" />
  </>,
)

export const ShieldIcon = icon(
  <>
    <path d="M12 3 5 6v6c0 4.5 3 8 7 9 4-1 7-4.5 7-9V6z" />
    <path d="m9.2 12 2 2 3.6-4" />
  </>,
)

export const BuildingIcon = icon(
  <>
    <path d="M4 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16" />
    <path d="M14 9h4a2 2 0 0 1 2 2v10" />
    <path d="M2 21h20" />
    <path d="M8 7h2M8 11h2M8 15h2" />
  </>,
)

export const LedgerIcon = icon(
  <>
    <path d="M5 4h13a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5z" />
    <path d="M5 4a2 2 0 0 0 0 4h2" />
    <path d="M11 9h5M11 13h5M11 17h3" />
  </>,
)

// ── Actions / affordances ───────────────────────────────────────────────────

export const SearchIcon = icon(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </>,
)

export const PlusIcon = icon(<path d="M12 5v14M5 12h14" />)
export const MinusIcon = icon(<path d="M5 12h14" />)
export const XIcon = icon(<path d="M6 6l12 12M18 6 6 18" />)
export const CheckIcon = icon(<path d="m4.5 12.5 5 5 10-11" />)
export const ChevronDownIcon = icon(<path d="m6 9 6 6 6-6" />)
export const ChevronRightIcon = icon(<path d="m9 6 6 6-6 6" />)
export const ChevronLeftIcon = icon(<path d="m15 6-6 6 6 6" />)
export const ArrowRightIcon = icon(<path d="M4 12h15m-6-6 6 6-6 6" />)
export const ArrowLeftIcon = icon(<path d="M20 12H5m6-6-6 6 6 6" />)
export const ArrowUpIcon = icon(<path d="M12 20V5m-6 6 6-6 6 6" />)
export const ArrowDownIcon = icon(<path d="M12 4v15m6-6-6 6-6-6" />)
export const MenuIcon = icon(<path d="M4 7h16M4 12h16M4 17h16" />)
export const MoreIcon = icon(
  <>
    <circle cx="12" cy="5" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="19" r="1.4" fill="currentColor" stroke="none" />
  </>,
)

export const FilterIcon = icon(<path d="M3 5h18l-7 8v6l-4 2v-8z" />)
export const SlidersIcon = icon(
  <>
    <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h10M18 18h2" />
    <circle cx="16" cy="6" r="2" />
    <circle cx="10" cy="12" r="2" />
    <circle cx="16" cy="18" r="2" />
  </>,
)

export const DownloadIcon = icon(
  <>
    <path d="M12 3v12" />
    <path d="m7 11 5 5 5-5" />
    <path d="M4 20h16" />
  </>,
)

export const UploadIcon = icon(
  <>
    <path d="M12 20V8" />
    <path d="m7 12 5-5 5 5" />
    <path d="M4 4h16" />
  </>,
)

export const ExternalIcon = icon(
  <>
    <path d="M14 4h6v6" />
    <path d="M20 4 10 14" />
    <path d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" />
  </>,
)

export const CopyIcon = icon(
  <>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2" />
  </>,
)

export const RefreshIcon = icon(
  <>
    <path d="M20 11a8 8 0 0 0-14-4.5L4 9" />
    <path d="M4 5v4h4" />
    <path d="M4 13a8 8 0 0 0 14 4.5L20 15" />
    <path d="M20 19v-4h-4" />
  </>,
)

export const LogOutIcon = icon(
  <>
    <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
    <path d="M11 16 15 12 11 8" />
    <path d="M15 12H4" />
  </>,
)

export const EyeIcon = icon(
  <>
    <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12" />
    <circle cx="12" cy="12" r="2.8" />
  </>,
)

export const EyeOffIcon = icon(
  <>
    <path d="M10.6 6.2A8.8 8.8 0 0 1 12 6c6.5 0 10 6 10 6a15.8 15.8 0 0 1-3.1 3.8" />
    <path d="M6.4 7.6A15.5 15.5 0 0 0 2 12s3.5 6 10 6a9.7 9.7 0 0 0 4.2-.9" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <path d="m3 3 18 18" />
  </>,
)

/* Google's mark keeps its own four brand colours — a monochrome G on a
   "Continue with Google" button reads as a generic icon, and their branding
   terms ask for the real one. */
export function GoogleIcon({ size = 18, ...rest }: IconProps): React.ReactNode {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...rest}>
      <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.05H12v3.88h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.89-1.74 2.98-4.3 2.98-7.36" />
      <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.62-2.41l-3.24-2.51c-.9.6-2.04.96-3.38.96-2.6 0-4.8-1.76-5.59-4.12H3.06v2.59A10 10 0 0 0 12 22" />
      <path fill="#FBBC05" d="M6.41 13.92a6 6 0 0 1 0-3.83V7.5H3.06a10 10 0 0 0 0 9z" />
      <path fill="#EA4335" d="M12 5.95c1.47 0 2.79.5 3.83 1.5l2.87-2.87C16.95 2.99 14.7 2 12 2a10 10 0 0 0-8.94 5.5l3.35 2.59C7.2 7.72 9.4 5.95 12 5.95" />
    </svg>
  )
}

export const CalendarIcon = icon(
  <>
    <rect x="3" y="5" width="18" height="16" rx="2.5" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </>,
)

export const ClockIcon = icon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5.2l3.2 2" />
  </>,
)

export const InfoIcon = icon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </>,
)

export const AlertIcon = icon(
  <>
    <path d="M10.3 3.9 2.6 17a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </>,
)

export const CheckCircleIcon = icon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.2 12.2 2.6 2.6 5-5.6" />
  </>,
)

export const XCircleIcon = icon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m9 9 6 6M15 9l-6 6" />
  </>,
)

export const CircleIcon = icon(<circle cx="12" cy="12" r="9" />)

export const LockIcon = icon(
  <>
    <rect x="4" y="10" width="16" height="11" rx="2.5" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </>,
)

export const MailIcon = icon(
  <>
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="m3 7 8.1 5.4a1.6 1.6 0 0 0 1.8 0L21 7" />
  </>,
)

export const PhoneIcon = icon(
  <path d="M6.6 3h3l1.5 4-2 1.4a12.5 12.5 0 0 0 6.5 6.5l1.4-2 4 1.5v3a2 2 0 0 1-2.2 2A17.5 17.5 0 0 1 4.6 5.2 2 2 0 0 1 6.6 3" />,
)

export const MapPinIcon = icon(
  <>
    <path d="M12 22s7-6.2 7-11.5a7 7 0 1 0-14 0C5 15.8 12 22 12 22" />
    <circle cx="12" cy="10.5" r="2.6" />
  </>,
)

export const TruckIcon = icon(
  <>
    <path d="M2 6h11v11H2z" />
    <path d="M13 9h4l4 4v4h-8z" />
    <circle cx="7" cy="18" r="1.8" />
    <circle cx="17" cy="18" r="1.8" />
  </>,
)

export const HeadsetIcon = icon(
  <>
    <path d="M4 13v-1a8 8 0 0 1 16 0v1" />
    <path d="M4 13h2.5a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
    <path d="M20 13h-2.5a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1H19a1 1 0 0 0 1-1z" />
    <path d="M18 19v.5a2.5 2.5 0 0 1-2.5 2.5H13" />
  </>,
)

export const ChatIcon = icon(<path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-5.4A8 8 0 1 1 21 12" />)
export const BookIcon = icon(
  <>
    <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5z" />
    <path d="M20 18v3H6.5A2.5 2.5 0 0 1 4 18.5" />
  </>,
)

export const CodeIcon = icon(<path d="m9 8-5 4 5 4M15 8l5 4-5 4" />)
export const SparkIcon = icon(
  <>
    <path d="M12 3v3M12 18v3M4.2 7.5 6.8 9M17.2 15l2.6 1.5M4.2 16.5 6.8 15M17.2 9l2.6-1.5" />
    <circle cx="12" cy="12" r="3.5" />
  </>,
)

export const RocketIcon = icon(
  <>
    <path d="M13.5 4.5C17 3 20 3 20 3s0 3-1.5 6.5c-1.2 2.8-4.3 5.6-6.2 7.1L8 13.7c1.5-1.9 4.3-5 5.5-6.2z" />
    <circle cx="14.8" cy="9.2" r="1.4" />
    <path d="m8 13.7-3 .8.8-3M6 17s-1.5.5-2 3c2.5-.5 3-2 3-2z" />
  </>,
)

export const TargetIcon = icon(
  <>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="4.5" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
  </>,
)

export const TrendUpIcon = icon(
  <>
    <path d="M3 17 9.5 10.5l3.5 3.5L21 6" />
    <path d="M15 6h6v6" />
  </>,
)

export const RupeeIcon = icon(<path d="M7 4h10M7 8h10M17 4c0 3-2.5 4-6 4h-1l7 8H7" />)
export const KeyIcon = icon(
  <>
    <circle cx="8" cy="14" r="4" />
    <path d="m11 11 9-9M17 5l2 2M15 7l2 2" />
  </>,
)

export const WebhookIcon = icon(
  <>
    <circle cx="7" cy="17" r="3" />
    <circle cx="17" cy="17" r="3" />
    <circle cx="12" cy="6" r="3" />
    <path d="M10.5 8.6 8.5 14M13.5 8.6 15.5 14M10 17h4" />
  </>,
)

export const PaletteIcon = icon(
  <>
    <path d="M12 21a9 9 0 1 1 9-9c0 2-1.6 2.6-3 2.6h-1.5a2 2 0 0 0-1.3 3.5A2 2 0 0 1 12 21" />
    <circle cx="7.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="9.5" cy="8" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="14.5" cy="8" r="1.1" fill="currentColor" stroke="none" />
  </>,
)

export const CursorIcon = icon(<path d="m5 3 14 8.5-6.2 1.4L9.5 19z" />)
export const ReceiptIcon = icon(
  <>
    <path d="M5 3h14v18l-2.3-1.5L14.4 21l-2.4-1.5L9.6 21l-2.3-1.5L5 21z" />
    <path d="M9 8h6M9 12h6" />
  </>,
)

export const UndoIcon = icon(
  <>
    <path d="M4 9h9a5 5 0 0 1 0 10h-2" />
    <path d="M8 5 4 9l4 4" />
  </>,
)

export const BanIcon = icon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m5.6 5.6 12.8 12.8" />
  </>,
)

// ── Platform marks ──────────────────────────────────────────────────────────

/** Shopify's bag silhouette, simplified to a single path at this size. */
export const ShopifyIcon = icon(
  <>
    <path d="M6.5 6.2 4 20l8 2 8-2-2.2-13.6-3.2-.8-.8-2-2.7.5-.6 1.6z" />
    <path d="M9.5 5.9c.7-1.6 1.6-2.4 2.6-2.4" strokeWidth={1.4} stroke="var(--surface)" fill="none" />
    <path d="M13.6 10.2a3 3 0 0 0-1.9-.6c-1.3 0-1.8.7-1.8 1.4 0 1.7 2.8 1.7 2.8 3.5 0 1.1-.9 2-2.3 2a3.5 3.5 0 0 1-2.1-.7" strokeWidth={1.4} stroke="var(--surface)" fill="none" />
  </>,
  true,
)

/** The WooCommerce/WordPress "W" in a disc. */
export const WordPressIcon = icon(
  <>
    <circle cx="12" cy="12" r="9.5" />
    <path
      d="M6 9.2h1.7l1.5 5.3 1.6-5.3h1.4l1.6 5.3 1.5-5.3H18l-2.5 7.4h-1.6L12.3 11l-1.6 5.6H9.1z"
      fill="var(--surface)"
      stroke="none"
    />
  </>,
  true,
)

export const RazorpayIcon = icon(<path d="M4 20 14.5 5h4L8 20zM10 12h4.5L11 20H6.5z" />, true)

export const WhatsAppIcon = icon(
  <path d="M12 3a9 9 0 0 0-7.7 13.7L3 21l4.4-1.2A9 9 0 1 0 12 3m4.6 12.2c-.2.6-1.2 1.1-1.7 1.1-.4 0-1 .1-3-.8-2.5-1-4.1-3.6-4.2-3.8-.1-.2-1-1.3-1-2.5s.6-1.8.9-2c.2-.3.5-.3.6-.3h.5c.2 0 .4 0 .6.5l.8 1.9c.1.2 0 .4 0 .5l-.4.5c-.1.2-.3.3-.1.6.1.3.6 1.1 1.4 1.8 1 .8 1.7 1.1 2 1.2.2.1.4.1.5-.1l.7-.8c.2-.2.3-.2.6-.1l1.7.8c.3.1.4.2.4.3z" />,
  true,
)
